import React, { useState } from 'react';
import { Modal, Button, Form, Row, Col, Card } from 'react-bootstrap';
import { FaCopy, FaCheck, FaFilePdf, FaFileExcel, FaUsers, FaMoneyBillWave, FaReceipt, FaWhatsapp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { eventApi } from '../../../api/events';
import { useAuthContext } from '../../../context/AuthContext';
import apiClient from '../../../api/index';
import ExportUtility from '../../../components/ExportUtility/ExportUtility';

const QuickReportModal = ({ show, onHide, dashboardData, currentEvent }) => {
  const { isAuthenticated } = useAuthContext();

  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const [eventSummary, setEventSummary] = useState('');
  const [summaryLoading, setSummaryLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [exportingField, setExportingField] = useState(null);

  const [pledgesTotals, setPledgesTotals] = useState(null);

  const pledgeCols = [
    { header: 'Jina la Mgeni', dataKey: 'name' },
    { header: 'Simu', dataKey: 'phone' },
    { header: 'Ahadi (TZS)', dataKey: 'pledge' },
    { header: 'Kiasi Kilicholipwa (TZS)', dataKey: 'paid' },
    { header: 'Deni (TZS)', dataKey: 'remain' },
    { header: 'Hali ya Malipo', dataKey: 'status' }
  ];

  const fundCols = [
    { header: 'Mchangiaji', dataKey: 'name' },
    { header: 'Simu', dataKey: 'phone' },
    { header: 'Kiasi (TZS)', dataKey: 'amount' },
    { header: 'Njia ya Malipo', dataKey: 'channel' },
    { header: 'Tarehe', dataKey: 'date' }
  ];

  const budgetCols = [
    { header: 'Huduma/Bidhaa', dataKey: 'item_name' },
    { header: 'Kundi', dataKey: 'category' },
    { header: 'Kadirio (TZS)', dataKey: 'estimated_cost' },
    { header: 'Halisi (TZS)', dataKey: 'actual_cost' },
    { header: 'Kilicholipwa (TZS)', dataKey: 'paid_amount' },
    { header: 'Deni (TZS)', dataKey: 'balance' }
  ];

  const expCols = [
    { header: 'Matumizi', dataKey: 'item_name' },
    { header: 'Kundi', dataKey: 'category' },
    { header: 'Kiasi (TZS)', dataKey: 'amount' },
    { header: 'Mlipwaji', dataKey: 'paid_to' },
    { header: 'Tarehe', dataKey: 'date' }
  ];

  const fetchPledgesData = async () => {
    try {
      const response = await apiClient.get('/export/pledges/json');
      const dataPayload = response?.data;
      const rawData = dataPayload?.pledges || dataPayload?.data || dataPayload || [];
      return Array.isArray(rawData) ? rawData.map(item => {
        const pledgeAmt = parseFloat(item.pledge_amount || item.pledge || item.amount || 0);
        const collectedAmt = parseFloat(item.collected || item.paid || item.amount_paid || 0);
        const remainAmt = parseFloat(item.remain !== undefined ? item.remain : (pledgeAmt - collectedAmt));
        return {
          name: item.full_name || item.name || item.guest_name || 'N/A',
          phone: item.mobile || item.phone_number || item.phone || 'N/A',
          email: item.email || 'N/A',
          pledge: pledgeAmt.toLocaleString(),
          paid: collectedAmt.toLocaleString(),
          remain: remainAmt.toLocaleString(),
          status: item.status || (remainAmt <= 0 ? 'Imelipwa' : 'Inadaiwa')
        };
      }) : [];
    } catch (err) {
      console.error(err);
      toast.error('Imeshindwa kupakua data ya wageni');
      return [];
    }
  };

  const fetchFundsData = async () => {
    try {
      const response = await apiClient.get('/export/funds/json');
      const dataPayload = response?.data;
      const rawData = dataPayload?.funds || dataPayload?.contributions || dataPayload?.data || dataPayload || [];
      return Array.isArray(rawData) ? rawData.map(item => ({
        name: item.full_name || item.name || item.guest_name || 'N/A',
        phone: item.mobile || item.phone_number || item.phone || 'N/A',
        amount: parseFloat(item.amount || item.fund || item.amount_paid || 0).toLocaleString(),
        channel: item.channel || item.payment_channel || 'Mobile Money',
        date: item.date || item.created_at ? new Date(item.date || item.created_at).toLocaleDateString() : 'N/A'
      })) : [];
    } catch (err) {
      console.error(err);
      toast.error('Imeshindwa kupakua data ya michango');
      return [];
    }
  };

  const fetchBudgetData = async () => {
    try {
      const response = await apiClient.get('/export/budget/json');
      const dataPayload = response?.data;
      const rawData = dataPayload?.budget || dataPayload?.items || dataPayload?.data || dataPayload || [];
      return Array.isArray(rawData) ? rawData.map(item => {
        const est = parseFloat(item.estimated_cost || item.budget_amount || item.amount || 0);
        const act = parseFloat(item.actual_cost || item.actual_amount || item.cost || 0);
        const paid = parseFloat(item.paid_amount || item.amount_paid || 0);
        return {
          item_name: item.item_name || item.name || item.title || 'N/A',
          category: item.category || item.category_name || 'N/A',
          estimated_cost: est.toLocaleString(),
          actual_cost: act.toLocaleString(),
          paid_amount: paid.toLocaleString(),
          balance: (act > 0 ? act - paid : est - paid).toLocaleString()
        };
      }) : [];
    } catch (err) {
      console.error(err);
      toast.error('Imeshindwa kupakua data ya bajeti');
      return [];
    }
  };

  const fetchExpendituresData = async () => {
    try {
      const response = await apiClient.get('/export/expenditures/json');
      const dataPayload = response?.data;
      const rawData = dataPayload?.expenditures || dataPayload?.expenses || dataPayload?.data || dataPayload || [];
      return Array.isArray(rawData) ? rawData.map(item => ({
        item_name: item.item_name || item.name || item.description || 'N/A',
        category: item.category || item.category_name || 'N/A',
        amount: parseFloat(item.amount || item.cost || item.actual_cost || 0).toLocaleString(),
        paid_to: item.paid_to || item.vendor || item.provider || 'N/A',
        date: item.date || item.created_at ? new Date(item.date || item.created_at).toLocaleDateString() : 'N/A'
      })) : [];
    } catch (err) {
      console.error(err);
      toast.error('Imeshindwa kupakua data ya matumizi');
      return [];
    }
  };

  const fetchEventSummary = async () => {
    setSummaryLoading(true);
    try {
      const response = await eventApi.getEventSummary();
      const data = response?.data || response || {};

      if (typeof data === 'string') {
        setEventSummary(data);
      } else if (data?.summary || data?.summary_text) {
        setEventSummary(data.summary || data.summary_text);
      } else if (typeof data === 'object') {
        const formatted = Object.entries(data).map(([key, value]) => {
          const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
          return `${label}: ${value}`;
        }).join('\n');
        setEventSummary(formatted || 'No summary available.');
      } else {
        setEventSummary('No summary available.');
      }
    } catch (err) {
      console.error('Failed to load event summary:', err);
      setEventSummary('Unable to load event summary. Please try again later.');
    } finally {
      setSummaryLoading(false);
    }
  };

  const fetchPledgesTotals = async () => {
    try {
      const response = await apiClient.get('/export/pledges/json');
      if (response?.data?.totals) {
        setPledgesTotals(response.data.totals);
      }
    } catch (err) {
      console.error('Failed to load pledges totals:', err);
    }
  };

  React.useEffect(() => {
    if (show && isAuthenticated) {
      fetchEventSummary();
      fetchPledgesTotals();
    }
  }, [show, isAuthenticated]);

  const getSummaryText = () => {
    if (summaryLoading) return 'Loading event summary...';
    if (eventSummary) return eventSummary;

    return `📊 EVENT SUMMARY - ${currentEvent?.event_title || 'My Event'}\n\n` +
      `📝 Total Pledges: TZS ${(dashboardData?.totalPledges || 0).toLocaleString()}\n` +
      `👥 Number of Pledges: ${dashboardData?.totalPledgesCount || 0}\n` +
      `💰 Total Contributions: TZS ${(dashboardData?.totalFunds || 0).toLocaleString()}\n` +
      `👤 Contributors: ${dashboardData?.totalFundsCount || 0}\n` +
      `📋 Total Budget: TZS ${(dashboardData?.totalBudget || 0).toLocaleString()}\n` +
      `💸 Total Expenditures: TZS ${(dashboardData?.totalExpenditures || 0).toLocaleString()}\n` +
      `📱 SMS Credits Available: ${dashboardData?.smsCredits || 0}\n` +
      `💬 WhatsApp Credits Available: ${dashboardData?.whatsappCredits || 0}\n\n` +
      `📅 Last Updated: ${new Date().toLocaleString()}\n` +
      `🏷️ Event: ${currentEvent?.event_type || 'N/A'}\n` +
      `📆 Event Date: ${currentEvent?.event_date ? new Date(currentEvent.event_date).toLocaleDateString() : 'Not set'}\n\n` +
      `--- Generated by HarusiOnline Dashboard ---`;
  };

  const handleCopyToClipboard = () => {
    const summaryText = getSummaryText();
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(summaryText).then(() => {
        setCopiedToClipboard(true);
        toast.success('Summary copied to clipboard!');
        setTimeout(() => setCopiedToClipboard(false), 2000);
      }).catch((err) => {
        console.error('Failed to copy:', err);
        fallbackCopyToClipboard(summaryText);
      });
    } else {
      fallbackCopyToClipboard(summaryText);
    }
  };

  const fallbackCopyToClipboard = (text) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      setCopiedToClipboard(true);
      toast.success('Summary copied to clipboard!');
      setTimeout(() => setCopiedToClipboard(false), 2000);
    } catch (err) {
      console.error('Fallback copy failed:', err);
      toast.error('Failed to copy to clipboard');
    }
    document.body.removeChild(textarea);
  };

  const handleDownloadImportTemplate = () => {
    const templateData = [{
      name: 'Mr and Mrs Lucas', phone_number: '255123456789', email: 'example@email.com',
      pledge: '100000', fund: '50000', CardType: 'Single/Double'
    }];
    const templateCols = [{ wch: 30 }, { wch: 20 }, { wch: 30 }, { wch: 15 }, { wch: 15 }, { wch: 15 }];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(templateData);
    ws['!cols'] = templateCols;
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, `wageni_import_template.xlsx`);
    toast.info('Template downloaded');
  };

  if (!isAuthenticated) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Quick Reports & Summary</Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-light">
        {/* Summary Preview - UP */}
        <Form.Group className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <Form.Label className="fw-bold mb-0 text-secondary text-uppercase" style={{ fontSize: '0.85rem', letterSpacing: '0.5px' }}>
              Summary Preview
            </Form.Label>
            <Button 
              variant="success" 
              size="sm"
              onClick={handleCopyToClipboard}
              className="d-flex align-items-center gap-2 px-3 rounded-pill shadow-sm"
              style={{ fontWeight: '600', backgroundColor: '#25D366', borderColor: '#25D366', color: '#ffffff' }}
            >
              {copiedToClipboard ? <><FaCheck /> Copied!</> : <><FaWhatsapp size={16} /> Share Summary</>}
            </Button>
          </div>
          <Card className="border-0 shadow-sm">
            <Form.Control
              as="textarea"
              rows={9}
              value={getSummaryText()}
              readOnly
              style={{ fontSize: '0.9rem', backgroundColor: '#ffffff', resize: 'none', border: '1px solid #e2e8f0' }}
            />
          </Card>
        </Form.Group>

        {/* Download Reports - DOWN */}
        <h6 className="fw-bold mb-3 text-secondary text-uppercase" style={{ fontSize: '0.85rem', letterSpacing: '0.5px' }}>
          Download Reports
        </h6>
        <div className="mb-2">
          <div className="d-flex flex-column gap-2">
            
            {/* Pledges */}
            <div className="d-flex flex-wrap align-items-center justify-content-between p-2 rounded" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
              <div className="d-flex align-items-center gap-2">
                <FaUsers className="text-secondary" />
                <span className="fw-semibold" style={{ fontSize: '0.9rem' }}>Guests & Pledges</span>
              </div>
              <div className="d-flex gap-2">
                <ExportUtility
                  fetchData={fetchPledgesData}
                  columns={pledgeCols}
                  filename="Pledges_Report"
                  excelOnlyButton={true}
                  excelButtonText="Excel"
                />
                <ExportUtility
                  fetchData={fetchPledgesData}
                  columns={pledgeCols}
                  filename="Pledges_Report"
                  pdfTitle={`Ripoti ya Wageni na Ahadi - ${currentEvent?.event_title || ''}`}
                  pdfSubtitle={pledgesTotals ? `Jumla ya Ahadi: TZS ${pledgesTotals.total_pledges?.toLocaleString()} | Zilizokusanywa: TZS ${pledgesTotals.total_collected?.toLocaleString()} | Baki: TZS ${pledgesTotals.total_remaining?.toLocaleString()}` : ''}
                  pdfOnlyButton={true}
                  pdfButtonText="PDF"
                />
              </div>
            </div>

            {/* Funds */}
            <div className="d-flex flex-wrap align-items-center justify-content-between p-2 rounded" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
              <div className="d-flex align-items-center gap-2">
                <FaMoneyBillWave className="text-secondary" />
                <span className="fw-semibold" style={{ fontSize: '0.9rem' }}>Funds & Contributions</span>
              </div>
              <div className="d-flex gap-2">
                <ExportUtility
                  fetchData={fetchFundsData}
                  columns={fundCols}
                  filename="Funds_Report"
                  excelOnlyButton={true}
                  excelButtonText="Excel"
                />
                <ExportUtility
                  fetchData={fetchFundsData}
                  columns={fundCols}
                  filename="Funds_Report"
                  pdfTitle={`Ripoti ya Michango na Mapato - ${currentEvent?.event_title || ''}`}
                  pdfOnlyButton={true}
                  pdfButtonText="PDF"
                />
              </div>
            </div>

            {/* Budget */}
            <div className="d-flex flex-wrap align-items-center justify-content-between p-2 rounded" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
              <div className="d-flex align-items-center gap-2">
                <FaReceipt className="text-secondary" />
                <span className="fw-semibold" style={{ fontSize: '0.9rem' }}>Budget</span>
              </div>
              <div className="d-flex gap-2">
                <ExportUtility
                  fetchData={fetchBudgetData}
                  columns={budgetCols}
                  filename="Budget_Report"
                  excelOnlyButton={true}
                  excelButtonText="Excel"
                />
                <ExportUtility
                  fetchData={fetchBudgetData}
                  columns={budgetCols}
                  filename="Budget_Report"
                  pdfTitle={`Ripoti ya Bajeti - ${currentEvent?.event_title || ''}`}
                  pdfOnlyButton={true}
                  pdfButtonText="PDF"
                />
              </div>
            </div>

            {/* Expenses */}
            <div className="d-flex flex-wrap align-items-center justify-content-between p-2 rounded" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
              <div className="d-flex align-items-center gap-2">
                <FaReceipt className="text-secondary" />
                <span className="fw-semibold" style={{ fontSize: '0.9rem' }}>Expenses</span>
              </div>
              <div className="d-flex gap-2">
                <ExportUtility
                  fetchData={fetchExpendituresData}
                  columns={expCols}
                  filename="Expenses_Report"
                  excelOnlyButton={true}
                  excelButtonText="Excel"
                />
                <ExportUtility
                  fetchData={fetchExpendituresData}
                  columns={expCols}
                  filename="Expenses_Report"
                  pdfTitle={`Ripoti ya Matumizi - ${currentEvent?.event_title || ''}`}
                  pdfOnlyButton={true}
                  pdfButtonText="PDF"
                />
              </div>
            </div>

            {/* Import Template */}
            <div className="d-flex flex-wrap align-items-center justify-content-between p-2 rounded mt-2" style={{ backgroundColor: '#ffffff', border: '1px dashed #ced4da' }}>
              <div className="d-flex align-items-center gap-2">
                <span className="fw-semibold text-muted" style={{ fontSize: '0.85rem' }}>Need to import guests?</span>
              </div>
              <Button variant="outline-secondary" size="sm" className="d-flex align-items-center gap-1" onClick={handleDownloadImportTemplate} style={{ fontSize: '0.8rem' }}>
                <FaFileExcel /> Download Import Template
              </Button>
            </div>

          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default QuickReportModal;
